import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  Link
} from '@mui/material';
import {
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Build as ToolIcon,
  CheckCircle as ConfirmIcon,
  Cancel as RejectIcon
} from '@mui/icons-material';
import Markdown, { defaultUrlTransform } from 'react-markdown';
import TassSourceCitation from './TassSourceCitation';
import TassFeedbackButton from './TassFeedbackButton';
import TassConversationSegments from './TassConversationSegments';

// Allow tass: protocol URLs through react-markdown's URL sanitizer
const tassUrlTransform = (url) => {
  if (url.startsWith('tass:')) return url;
  return defaultUrlTransform(url);
};

const tassAvatarSrc = '/tass-avatar.jpg?v=2';

const PERMISSION_COLORS = {
  read: 'default',
  safe_action: 'info',
  confirm_required: 'warning'
};

function ConfidenceDots({ confidence }) {
  if (confidence == null) return null;
  const filled = Math.round(confidence * 5);
  const label = filled <= 1 ? 'Low' : filled <= 3 ? 'Medium' : filled <= 4 ? 'High' : 'Very High';

  return (
    <Tooltip title={`Confidence: ${label} (${Math.round(confidence * 100)}%)`}>
      <Box display="inline-flex" alignItems="center" gap={0.25} sx={{ ml: 1 }}>
        {[1, 2, 3, 4, 5].map((dot) => (
          <Box
            key={dot}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: dot <= filled ? 'info.main' : 'grey.300'
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

function TassMessage({ message, onConfirmTool, onFeedback, onSelectClient }) {
  const [confirmLoading, setConfirmLoading] = useState(false);

  const isTherapist = message.role === 'therapist';
  const isTass = message.role === 'tass';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return (
      <Box sx={{ textAlign: 'center', my: 1, px: 2 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.7rem' }}
        >
          {message.content}
        </Typography>
      </Box>
    );
  }

  const handleConfirm = async (approved) => {
    if (!message.pendingConfirmation || confirmLoading) return;
    setConfirmLoading(true);
    try {
      await onConfirmTool(message.pendingConfirmation.toolCallId, approved);
    } finally {
      setConfirmLoading(false);
    }
  };

  // MUI-styled markdown components
  const markdownComponents = {
    p: ({ children }) => (
      <Typography variant="body2" sx={{ mb: 0.75, '&:last-child': { mb: 0 }, lineHeight: 1.5 }}>
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    em: ({ children }) => (
      <Typography component="span" variant="body2" sx={{ fontStyle: 'italic' }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{children}</Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.75, mb: 0.25 }}>{children}</Typography>
    ),
    h4: ({ children }) => (
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, mb: 0.25 }}>{children}</Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0.75, '& li': { mb: 0.25 } }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 0.75, '& li': { mb: 0.25 } }}>{children}</Box>
    ),
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ lineHeight: 1.5 }}>{children}</Typography>
    ),
    code: ({ inline, children }) => (
      inline ? (
        <Typography
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8em',
            backgroundColor: 'action.hover',
            borderRadius: 0.5,
            px: 0.5,
            py: 0.125
          }}
        >
          {children}
        </Typography>
      ) : (
        <Box
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            backgroundColor: 'action.hover',
            borderRadius: 1,
            p: 1,
            my: 0.5,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <code>{children}</code>
        </Box>
      )
    ),
    a: ({ href, children, node, ...rest }) => {
      // Detect tass:client: links for clickable client names
      if (href && href.startsWith('tass:client:')) {
        const clientId = href.replace('tass:client:', '');
        const clientName = typeof children === 'string' ? children
          : Array.isArray(children) ? children.join('') : String(children);
        return (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onSelectClient) onSelectClient(clientId, clientName);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (onSelectClient) onSelectClient(clientId, clientName);
              }
            }}
            style={{
              fontWeight: 600,
              cursor: 'pointer',
              color: '#1976d2',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted'
            }}
          >
            {children}
          </span>
        );
      }
      return (
        <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 'inherit' }}>
          {children}
        </Link>
      );
    },
    blockquote: ({ children }) => (
      <Box
        sx={{
          borderLeft: 3,
          borderColor: 'primary.light',
          pl: 1.5,
          my: 0.5,
          color: 'text.secondary'
        }}
      >
        {children}
      </Box>
    ),
    hr: () => <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1 }} />
  };

  // Render content with markdown formatting and inline source citations
  const renderContent = () => {
    const { content, sourceCitations } = message;

    const renderedMarkdown = (
      <Box sx={{ wordBreak: 'break-word', '& > *:first-of-type': { mt: 0 } }}>
        <Markdown components={markdownComponents} urlTransform={tassUrlTransform}>{content || ''}</Markdown>
      </Box>
    );

    if (!sourceCitations || sourceCitations.length === 0) {
      return renderedMarkdown;
    }

    return (
      <Box>
        {renderedMarkdown}
        <Box display="flex" flexWrap="wrap" gap={0.25} sx={{ mt: 0.5 }}>
          {sourceCitations.map((citation, idx) => (
            <TassSourceCitation key={idx} citation={citation} />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isTherapist ? 'flex-end' : 'flex-start',
        mb: 1.5,
        px: 1.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          maxWidth: '85%',
          flexDirection: isTherapist ? 'row-reverse' : 'row'
        }}
      >
        <Avatar
          src={isTass ? tassAvatarSrc : undefined}
          sx={{
            bgcolor: isTherapist ? 'primary.main' : 'transparent',
            width: 32,
            height: 32,
            mx: 0.75
          }}
        >
          {isTherapist && <PersonIcon sx={{ fontSize: 16 }} />}
        </Avatar>

        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            bgcolor: isTherapist ? 'primary.light' : 'grey.50',
            color: isTherapist ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2
          }}
        >
          {/* Header */}
          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
              {isTherapist ? 'You' : 'TASS'}
            </Typography>
            <Typography variant="caption" sx={{ ml: 0.75, opacity: 0.6, fontSize: '0.65rem' }}>
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            {isTass && <ConfidenceDots confidence={message.confidence} />}
          </Box>

          {/* Content */}
          {renderContent()}

          {/* Conversation Segments (from search_conversation) */}
          {message.conversationSegments && (
            <TassConversationSegments data={message.conversationSegments} />
          )}

          {/* Tool Calls */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {message.toolCalls.map((tool, idx) => (
                <Accordion
                  key={idx}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '4px !important',
                    mb: 0.5
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                    sx={{ minHeight: 28, px: 1, '& .MuiAccordionSummary-content': { my: 0.25 } }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ToolIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                        {tool.toolName}
                      </Typography>
                      <Chip
                        label={tool.permissionLevel || 'read'}
                        size="small"
                        color={PERMISSION_COLORS[tool.permissionLevel] || 'default'}
                        sx={{ height: 16, fontSize: '0.55rem' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1, pt: 0, pb: 0.5 }}>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontSize: '0.6rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        maxHeight: 120,
                        overflowY: 'auto'
                      }}
                    >
                      {typeof tool.output === 'string' ? tool.output : JSON.stringify(tool.output, null, 2)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* Pending Confirmation */}
          {message.pendingConfirmation && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1,
                backgroundColor: 'warning.50',
                border: '1px solid',
                borderColor: 'warning.light'
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Requires confirmation
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                {message.pendingConfirmation.description}
              </Typography>
              {message.pendingConfirmation.estimatedCost && (
                <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary' }}>
                  Est. cost: {message.pendingConfirmation.estimatedCost}
                </Typography>
              )}
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<ConfirmIcon sx={{ fontSize: 14 }} />}
                  disabled={confirmLoading}
                  onClick={() => handleConfirm(true)}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
                >
                  Confirm
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
                  disabled={confirmLoading}
                  onClick={() => handleConfirm(false)}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )}

          {/* Feedback button on TASS messages */}
          {isTass && onFeedback && (
            <Box sx={{ mt: 0.5, textAlign: 'right' }}>
              <TassFeedbackButton onFeedback={onFeedback} />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default TassMessage;
